import { useEffect, useState, type ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

/**
 * AuthCallback handles the SSO callback from DSAccount.
 * This page receives the authorization code and exchanges it for a session.
 */
export function AuthCallback(): ReactNode {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      try {
        // Get code and state from URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (!code) {
          // Check if there's an error from DSAccount
          const errorParam = params.get('error');
          if (errorParam) {
            throw new Error(errorParam === 'access_denied' 
              ? 'Access was denied. Please try again.'
              : `Authentication failed: ${errorParam}`);
          }
          throw new Error('No authorization code received');
        }

        // Verify state matches what we stored
        const savedState = sessionStorage.getItem('sso_state');
        if (state !== savedState) {
          console.warn('State mismatch - possible CSRF attempt');
          // Still proceed but log it - backend will also validate
        }

        // Exchange code for session via backend
        const response = await fetch(`${API_BASE}/auth/sso-callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, state: savedState || state }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Authentication failed');
        }

        const data = await response.json();
        
        // Clear stored state
        sessionStorage.removeItem('sso_state');
        
        // Get stored redirect URL or use default
        const redirectUrl = sessionStorage.getItem('sso_redirect') || '/';
        sessionStorage.removeItem('sso_redirect');

        setStatus('success');
        
        // Redirect to the original page
        setTimeout(() => {
          window.location.href = data.return_url || redirectUrl;
        }, 500);

      } catch (err) {
        console.error('SSO callback error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    }

    handleCallback();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Completing sign in...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card text-center">
          <div className="text-green-500 text-4xl mb-4">✓</div>
          <p className="text-gray-600">Signed in successfully. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card text-center max-w-md">
        <div className="text-red-500 text-4xl mb-4">✕</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign In Failed</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => window.location.href = '/'}
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

export default AuthCallback;
